/**
 * Journal AI Service
 * 
 * Handles AI generation requests for Learning Journals.
 * Uses Server-Sent Events (SSE) for streaming responses.
 * Routes through main backend API (with auth) to content-brain.
 */

import { config } from '@/lib/config';
import { supabase } from '@/integrations/supabase/client';

// Backend API base URL - uses main backend (not content-brain directly)
const API_BASE_URL = config.BACKEND_API_URL;

// ================== TYPES ==================

export interface AnswerMetadata {
  summary: string;
  key_points: string[];
  related_topics: string[];
  confidence: number;
}

export interface AskQuestionRequest {
  journal_id: string;
  question: string;
  source_block_ids?: string[];
}

export interface AskQuestionResponse {
  answer: string;
  metadata: AnswerMetadata;
}

// SSE Event Types for streaming answer
export type AskStreamEvent =
  | { type: 'text'; content: string }
  | { type: 'metadata'; data: AnswerMetadata }
  | { type: 'complete'; answer: string }
  | { type: 'error'; message: string };

// ================== ASK QUESTION (STREAMING) ==================

/**
 * Ask a question with streaming response.
 * 
 * @param request - Question request with journal context
 * @param onEvent - Callback for each SSE event
 * @returns Promise that resolves when streaming is complete
 * 
 * @example
 * ```ts
 * let fullText = '';
 * await askQuestionStreaming(
 *   { journal_id: 'xxx', question: 'What is photosynthesis?' },
 *   (event) => {
 *     if (event.type === 'text') {
 *       fullText += event.content;
 *       setDisplayText(fullText);
 *     } else if (event.type === 'metadata') {
 *       setMetadata(event.data);
 *     } else if (event.type === 'complete') {
 *       setIsComplete(true);
 *     }
 *   }
 * );
 * ```
 */
export async function askQuestionStreaming(
  request: AskQuestionRequest,
  onEvent: (event: AskStreamEvent) => void
): Promise<void> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/journals/ask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI request failed: ${response.status} - ${errorText}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error('No response body');
  }

  const decoder = new TextDecoder();
  let buffer = '';

  try {
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });

      // Parse SSE events from buffer
      const lines = buffer.split('\n');
      buffer = lines.pop() || ''; // Keep incomplete line in buffer

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            const event = JSON.parse(line.slice(6)) as AskStreamEvent;
            onEvent(event);
          } catch (e) {
            console.error('Failed to parse SSE event:', line, e);
          }
        }
      }
    }
  } finally {
    reader.releaseLock();
  }
}

// ================== ASK QUESTION (NON-STREAMING) ==================

/**
 * Ask a question and get complete response (non-streaming).
 * Useful for testing or when streaming is not needed.
 */
export async function askQuestion(
  request: AskQuestionRequest
): Promise<AskQuestionResponse> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(`${API_BASE_URL}/api/journals/ask-sync`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    },
    body: JSON.stringify(request),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`AI request failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}

// ================== UPDATE JOURNAL SUMMARY ==================

/**
 * Trigger journal summary regeneration.
 * Called after significant content changes.
 */
export async function updateJournalSummary(
  journalId: string
): Promise<{ status: string; summary?: Record<string, unknown>; journal_name_updated?: string }> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${API_BASE_URL}/api/journals/update-summary`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify({ journal_id: journalId }),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Summary update failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}

// ================== QUIZ GENERATION ==================

export type QuizType = 'mcq' | 'true_false' | 'fill_blank' | 'matching' | 'spot_mistake';
export type DifficultyLevel = 'easy' | 'medium' | 'hard';

export interface GenerateQuizRequest {
  journal_id: string;
  topic: string;
  quiz_type: QuizType;
  question_count: number;
  difficulty: DifficultyLevel;
}

export interface MCQItem {
  id: string;
  question: string;
  options: string[];
  answer: number;
  explanation: string;
}

export interface TrueFalseItem {
  id: string;
  statement: string;
  answer: 'True' | 'False';
  explanation: string;
}

export interface FillBlankItem {
  id: string;
  prompt: string;
  answer: string;
  accepted_variants: string[];
  explanation: string;
}

export interface MatchPairItem {
  id: string;
  left: string;
  right: string;
}

export interface SpotMistakeItem {
  id: string;
  prompt: string;
  choices: string[];
  answer: string;
  explanation: string;
}

export interface GeneratedQuiz {
  type: QuizType;
  title: string;
  items: MCQItem[] | TrueFalseItem[] | FillBlankItem[] | MatchPairItem[] | SpotMistakeItem[];
}

export interface GenerateQuizResponse {
  status: string;
  quiz: GeneratedQuiz;
}

/**
 * Generate a quiz based on journal content and topic.
 * 
 * @param request - Quiz generation parameters
 * @returns Promise with the generated quiz
 */
export async function generateQuiz(
  request: GenerateQuizRequest
): Promise<GenerateQuizResponse> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${API_BASE_URL}/api/journals/generate-quiz`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Quiz generation failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}


// ================== LEARNING PROGRESS ==================

export interface QuizResultReport {
  journal_id: string;
  quiz_type: QuizType;
  topic: string;
  difficulty: DifficultyLevel;
  total_questions: number;
  correct_answers: number;
}

export interface LearningProgress {
  quizzes_taken: number;
  total_questions_answered: number;
  total_correct: number;
  average_score: number;
  topics_mastered: string[];
  topics_struggling: string[];
  recent_quizzes: Array<{
    topic: string;
    quiz_type: QuizType;
    difficulty: DifficultyLevel;
    score: number;
    total_questions: number;
    timestamp: string;
  }>;
  last_updated: string | null;
}

/**
 * Report quiz completion to update learning progress.
 * Call this when user finishes a quiz (all questions answered).
 */
export async function reportQuizResult(
  report: QuizResultReport
): Promise<{ status: string; learning_progress: LearningProgress }> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${API_BASE_URL}/api/journals/report-quiz-result`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(report),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`Report quiz result failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}


// ================== YOUTUBE ANALYSIS ==================

export interface AnalyzeYouTubeRequest {
  journal_id: string;
  video_url: string;
  generate_summary?: boolean;
}

export interface YouTubeAnalysis {
  summary: string;
  key_points: string[];
  topics: string[];
  learning_objectives: string[];
}

export interface TranscriptSegment {
  text: string;
  start: number;  // Start time in seconds
  duration: number;
}

export interface AnalyzeYouTubeResponse {
  success: boolean;
  video_id: string;
  title?: string;
  transcript_available: boolean;
  transcript_length: number;
  transcript: TranscriptSegment[];  // Timestamped segments
  analysis?: YouTubeAnalysis;
  error?: string;
}

/**
 * Analyze a YouTube video for a learning journal.
 * Fetches transcript and generates AI summary.
 */
export async function analyzeYouTube(
  request: AnalyzeYouTubeRequest
): Promise<AnalyzeYouTubeResponse> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${API_BASE_URL}/api/journals/analyze-youtube`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`YouTube analysis failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}


// ================== PDF ANALYSIS ==================

export interface AnalyzePDFRequest {
  journal_id: string;
  block_id: string;
  storage_path: string;
  generate_summary?: boolean;
}

export interface PDFAnalysis {
  summary: string;
  key_points: string[];
  topics: string[];
  learning_objectives?: string[];
}

export interface PDFOutlineItem {
  level: number;
  title: string;
  page_start?: number;
  page_end?: number;
}

export interface PDFContext {
  outline?: PDFOutlineItem[];
  focus_areas: string[];
  glossary?: Array<{ term: string; definition: string }>;
  chapter_summaries?: Array<{ title: string; page_start?: number; summary: string }>;
}

export interface AnalyzePDFResponse {
  success: boolean;
  title?: string;
  document_type?: string;
  analysis?: PDFAnalysis;
  pdf_context?: PDFContext;
  text_assets?: {
    extracted_text_path: string;
    extracted_text_sha256: string;
    total_pages: number;
    total_chars: number;
  };
  processing?: {
    ocr_used: boolean;
    started_at?: string;
    completed_at?: string;
  };
  error?: string;
}

/**
 * Analyze a PDF document for a learning journal.
 * Extracts text, generates AI summary, and stores extracted text.
 */
export async function analyzePDF(
  request: AnalyzePDFRequest
): Promise<AnalyzePDFResponse> {
  // Get auth token from Supabase
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) {
    throw new Error('Authentication required');
  }

  const response = await fetch(
    `${API_BASE_URL}/api/journals/analyze-pdf`,
    {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${session.access_token}`,
      },
      body: JSON.stringify(request),
    }
  );

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(`PDF analysis failed: ${response.status} - ${errorText}`);
  }

  return response.json();
}


// ================== HEALTH CHECK ==================

/**
 * Check if the AI service is available.
 */
export async function checkAIServiceHealth(): Promise<boolean> {
  try {
    const response = await fetch(`${API_BASE_URL}/health`);
    return response.ok;
  } catch {
    return false;
  }
}

